/** Placement pur partagé par le renderer et la prévisualisation admin. */
export interface PlacementBox {
  id: number;
  name: string;
  parentId?: number | null;
  xPct: number;
  yPct: number;
  wPct: number;
  hPct: number;
}

export interface FormeScene {
  layout?: PlacementBox[];
  sol: { hauteurRatio: number; bandeRatio: number; penteDeg: number };
  /** Motifs des calques défilants, en points — cf. `MineCartArt`, contrat de répétition. */
  motifs: { sol: (periode: number) => number; fond: (periode: number) => number };
  vehicule: {
    appui: { largeurRatio: number; hauteurRatio: number };
    passager: { x: number; ancre: 'caisse' | 'sol'; offset: number };
  };
}

/**
 * Toutes les mesures de la scène, en points, dérivées de la boîte `scene` du layout.
 *
 * 🔴 **C'est ici que la pente peut casser le cadrage**, et nulle part ailleurs : une
 * inclinaison fait naître l'obstacle en contrebas et remonter le sol à gauche. Deux
 * invariants doivent tenir, et `verifierCadrage` les nomme — un test les vérifie pour
 * chaque thème, parce qu'un obstacle qui naît sous le cadre ne lève aucune erreur : il
 * est simplement invisible pendant deux secondes.
 */
export function geometrieScene(sceneW: number, sceneH: number, theme: FormeScene) {
  const scene = theme.layout?.find(b => b.name === 'scene');
  // Coordonnées absolues du positionneur, rapportées à la zone de scène.
  const repere = (name: string) => {
    const b = theme.layout?.find(b => b.name === name);
    if (!b || !scene) return undefined;
    return {
      x: ((b.xPct - scene.xPct) / scene.wPct) * sceneW,
      y: ((b.yPct - scene.yPct) / scene.hPct) * sceneH,
      w: (b.wPct / scene.wPct) * sceneW,
      h: (b.hPct / scene.hPct) * sceneH,
    };
  };
  const sol = repere('sol'),
    vehicle = repere('vehicule'),
    obstacle = repere('obstacle'),
    sortie = repere('sortie');
  /** Épaisseur DESSINÉE du sol, et ligne de contact — déclarées par le thème. */
  const bandeH = sol?.h ?? sceneH * theme.sol.bandeRatio;
  const solY = sol ? sol.y + bandeH * 0.42 : sceneH * theme.sol.hauteurRatio;
  /** La ligne de contact tombe à 42 % de la bande (contrat de `Voie`). */
  const bandeTop = solY - bandeH * 0.42;
  const solPeriode = Math.max(24, sceneH * 0.2);
  const fondPeriode = Math.max(120, sceneW * 0.55);
  /**
   * Le MOTIF d'un calque — la distance après laquelle son dessin se répète — et la
   * largeur qu'il faut dessiner pour la couvrir. Le défilement reboucle sur le motif :
   * un calque plus étroit que `sceneW + motif` découvrirait le vide à droite juste
   * avant de reboucler, et un modulo plus court ferait sauter le décor.
   */
  const motifSol = theme.motifs.sol(solPeriode);
  const motifFond = theme.motifs.fond(fondPeriode);
  const solW = sceneW + motifSol + solPeriode;
  const fondW = sceneW + motifFond + fondPeriode;

  /**
   * L'arrière-plan descend jusqu'à la LIGNE DE CONTACT : les pieds des arbres et des
   * étais sont alors enfouis sous le sol, qui est dessiné par-dessus.
   *
   * ⚠️ C'était une fraction de l'épaisseur de la bande (`+ 30 %`), ce qui revenait au
   * même tant que le sol était une mince bordure. Une piste inclinée se dessine
   * épaisse : la même fraction plantait les sapins une trentaine de points AU-DESSUS
   * de la neige, et ils flottaient. Un repère qui dépend de l'épaisseur de ce qu'on
   * dessine n'est pas un repère.
   */
  const fondH = solY;
  /** Pente : > 0 = le sol descend vers la droite. */
  const penteDeg = theme.sol.penteDeg;
  const tanPente = Math.tan((penteDeg * Math.PI) / 180);

  const cartW = vehicle?.w ?? sceneH * 0.62;
  const cartH = vehicle?.h ?? cartW * 0.6;
  const cartX = vehicle?.x ?? sceneW * 0.2;
  /** Boîte d'un appui : carrée pour une roue, longue et plate pour un ski. */
  const appuiW = cartW * theme.vehicule.appui.largeurRatio;
  const appuiH = appuiW * theme.vehicule.appui.hauteurRatio;
  const cartSolY = vehicle ? vehicle.y + cartH * 0.8 + appuiH * 0.56 : solY;
  const appuiCY = cartSolY - appuiH * 0.36;
  /** Le bas de la caisse est à 80 % de sa hauteur (cf. viewBox de `CorpsWagonnet`). */
  const corpsTop = appuiCY - appuiH * 0.2 - cartH * 0.8;
  const compagnonTaille = cartH * 0.95;
  /**
   * Le compagnon est assis DANS la caisse (ancre `caisse`) ou debout sur le sol
   * (ancre `sol`, pieds sur la ligne de contact) : à skis et à pied, il EST le
   * véhicule — l'accrocher à une caisse qui n'existe pas le ferait flotter.
   */
  const compagnonTop =
    (theme.vehicule.passager.ancre === 'caisse' ? corpsTop : cartSolY) -
    compagnonTaille * theme.vehicule.passager.offset;
  const compagnonLeft = cartW * theme.vehicule.passager.x;

  /**
   * Le POINT FIXE de la pente : le contact du véhicule avec le sol. Tout ce qui
   * appartient au sol pivote autour de lui, si bien que le compagnon reste posé à
   * `solY` quelle que soit l'inclinaison, et que l'obstacle arrive exactement à sa
   * hauteur. Prendre le centre de la scène ferait glisser le véhicule sous la neige.
   */
  const xRefSol = cartX + cartW * 0.42;

  /**
   * La JUPE : un aplat de la teinte du sol, sous la bande dessinée, qui comble ce que
   * l'inclinaison découvre à GAUCHE — le sol y remonte de `xRefSol × tan`, et le coin
   * bas laissait voir le décor peint sous la piste.
   *
   * 🔴 Elle existe pour que l'épaisseur DESSINÉE du sol reste un choix graphique : la
   * bande fine de la mine dessine un ballast de la bonne hauteur, et n'a pas à grossir
   * d'un cinquième parce qu'on a incliné la voie d'un degré. Sans elle, toute pente
   * nouvelle obligeait à re-régler l'épaisseur — deux réglages liés par rien.
   */
  const jupeH = Math.max(0, sceneH - (bandeTop + bandeH) + xRefSol * tanPente) + 4;

  const obH = obstacle?.h ?? sceneH * 0.42;
  const obW = obstacle?.w ?? obH * 1.2;
  /** Le sol de l'obstacle est à 96 % de sa boîte (cf. `Obstacle`). */
  const obTop = (obstacle?.y ?? solY - obH * 0.96) + 2;
  const obContactY = obTop + obH * 0.96;
  const spawnX = obstacle ? obstacle.x + obW * 1.2 : sceneW + obW * 0.2;
  /** À l'impact, l'obstacle TOUCHE l'avant du wagonnet — il ne rentre pas dedans. */
  const impactX = cartX + cartW * 0.88;

  // La rampe fait partie du DÉCOR : elle surgit juste devant l'obstacle et voyage
  // avec lui. Le wagonnet la gravit quand elle passe sous son avant, et elle file
  // ensuite sous lui avec l'obstacle. Fixée à l'écran, elle restait plantée devant
  // le wagonnet pendant qu'il sautait — l'inverse de ce qu'on veut lire.
  const rampeW = obW * 0.9;
  const rampeH = obH * 0.5;
  /** Décalage de la rampe par rapport au bord gauche de l'obstacle (son haut le touche). */
  const rampeDecalX = -rampeW * 0.96;
  const rampeTop = obContactY - 2 - rampeH * 0.975;

  const sortieW = sortie?.w ?? sceneW * 0.5;
  const sortieX = sortie?.x ?? sceneW - sortieW;
  const sortieY = sortie?.y ?? 0;
  const sortieH = sortie?.h ?? sceneH;
  const basePxParSec = sceneW * 0.5;
  return {
    sceneW,
    sceneH,
    bandeH,
    bandeTop,
    solY,
    solPeriode,
    fondPeriode,
    fondH,
    motifSol,
    motifFond,
    solW,
    fondW,
    jupeH,
    penteDeg,
    tanPente,
    xRefSol,
    cartW,
    cartH,
    cartX,
    cartSolY,
    appuiW,
    appuiH,
    appuiCY,
    corpsTop,
    compagnonTaille,
    compagnonTop,
    compagnonLeft,
    obH,
    obW,
    obTop,
    spawnX,
    impactX,
    rampeW,
    rampeH,
    rampeDecalX,
    rampeTop,
    sortieW,
    sortieX,
    sortieY,
    sortieH,
    basePxParSec,
  };
}

/**
 * Pose une illustration dans une boîte, à SON ratio, centrée et calée sur la ligne de
 * contact — `partAuDessus` disant quelle fraction d'elle se tient au-dessus.
 *
 * Une image ne déplace rien : la boîte et la ligne de contact restent celles de la
 * géométrie. Sans ce calcul, `resizeMode: 'contain'` centrerait l'image verticalement
 * dans la boîte — un rocher large flotterait au-dessus du sol de la moitié de ce qui lui
 * manque en hauteur, et rien ne le signalerait.
 *
 * 🔴 Le ratio vient de la DÉCLARATION du thème, jamais de `Image.resolveAssetSource` :
 * celui-ci ne rend pas de dimensions sur le web, et le défaut n'apparaissait donc que
 * sur une plateforme.
 */
export function poserSurLeSol(
  {
    ratio,
    enfoncement = 0,
    echelle = 1,
  }: { ratio: number; enfoncement?: number; echelle?: number },
  boiteW: number,
  yContact: number,
  partAuDessus = 1
) {
  let h = partAuDessus > 0 ? yContact / partAuDessus : yContact;
  let w = h * ratio;
  if (w > boiteW) {
    w = boiteW;
    h = w / ratio;
  }
  h *= echelle;
  w *= echelle;
  return {
    left: (boiteW - w) / 2,
    top: yContact - h * (partAuDessus - enfoncement),
    width: w,
    height: h,
  };
}

const OBSTACLES_VERTICAUX: ReadonlySet<string> = new Set<string>([
  'sapin',
  'barriere',
  'haie',
  'monstre',
  'stalactite',
]);

/** `true` si la gravité tient cette sorte droite (sapin), `false` si elle épouse le sol (rocher). */
export function estObstacleVertical(kind: string): boolean {
  return OBSTACLES_VERTICAUX.has(kind);
}

/**
 * Part de la hauteur d'un obstacle qui se tient AU-DESSUS de la ligne de contact.
 *
 * Presque tout se DRESSE sur le sol (1) ; un creux, lui, s'enfonce dessous. Poser une
 * crevasse comme un rocher — son bas sur la ligne — la fait flotter au-dessus de la piste
 * comme un tapis : le sol passe par le HAUT de son ouverture, et le reste descend vers
 * nous. Les rails brisés, eux, enjambent la coupure : ils la traversent.
 */
export function partAuDessusDuSol(kind: string): number {
  if (kind === 'crevasse') return 0;
  if (kind === 'trou') return 0.62;
  return 1;
}

export interface ObstacleIllustrationDefinition {
  id: string;
  label: string;
  kind: string;
  reference?: string;
  image?: { url: string; width: number; height: number };
  /** Hauteur de l’image en % de la scène, indépendante de son ratio. */
  heightPct?: number;
}
export function defaultObstacleIllustrations(config: any): ObstacleIllustrationDefinition[] {
  const category = config.projectiles ? 'projectiles' : 'obstacles';
  return (config.obstacles || config.projectiles).flatMap((kind: string) => {
    const value = config.images?.[category]?.[kind];
    const images = Array.isArray(value) ? value : [value];
    return images.map((image: any, i: number) => ({
      id: `base_${kind}_${i}`,
      label: `${kind}${images.length > 1 ? ` ${i + 1}` : ''}`,
      kind,
      ...(image
        ? { reference: `images.${category}.${kind}${Array.isArray(value) ? `.${i}` : ''}.source` }
        : {}),
    }));
  });
}
export function resolveObstacleIllustrations(
  config: any,
  definitions: ObstacleIllustrationDefinition[],
  imageSource: (image: NonNullable<ObstacleIllustrationDefinition['image']>) => any
) {
  return definitions.map(row => {
    const original = row.reference
      ?.slice(0, -7)
      .split('.')
      .reduce((v: any, k: string) => v?.[k], config);
    return {
      ...row,
      image: row.image
        ? { source: imageSource(row.image), ratio: row.image.width / row.image.height }
        : original,
    };
  });
}
/** Parcours de tout le banc : chaque illustration apparaît avant de recommencer. */
export function obstacleIllustrationForIndex<T>(
  index: number,
  seed: number,
  rows: readonly T[]
): T | undefined {
  return rows.length
    ? rows[(((index + seed) % rows.length) + rows.length) % rows.length]
    : undefined;
}
export function poserObstacle(
  image: { ratio: number; enfoncement?: number; echelle?: number },
  geo: ReturnType<typeof geometrieScene>,
  kind: string,
  heightPct?: number
) {
  const part = partAuDessusDuSol(kind);
  if (heightPct === undefined) return poserSurLeSol(image, geo.obW, geo.obH * 0.96, part);
  const height = (geo.sceneH * heightPct) / 100;
  const width = height * image.ratio;
  return {
    left: (geo.obW - width) / 2,
    top: geo.obH * 0.96 - height * (part - (image.enfoncement ?? 0)),
    width,
    height,
  };
}

/** Parcours continu du banc, même quand le nombre de projectiles change entre salves. */
export function obstacleIllustrationsForBatch<T>(
  cursor: number,
  count: number,
  seed: number,
  rows: readonly T[]
) {
  return {
    illustrations: Array.from({ length: count }, (_, i) =>
      obstacleIllustrationForIndex(cursor + i, seed, rows)!
    ),
    nextCursor: cursor + count,
  };
}
